import { usePaging } from '@/hooks/usePaginatedApi';
import styles from './Paging.module.css';

interface PagingProps {
	onPageChange?: () => void;
	className?: string;
	contextId?: string;
}

const Paging = (props: PagingProps) => {
	// Usa l'ID di contesto fornito o 'default' se non specificato
	const contextId = props.contextId || 'default';
	const { pagingOptions, totalPages, setPagingOptions } = usePaging(contextId);

	// Non mostrare la paginazione se c'è solo una pagina
	//if (totalPages <= 1) {
	//	return null;
	//}

	/* 
	Aggiorna il contesto con la nuova pagina selezionata
	1. L'utente fa clic su un pulsante di pagina nel componente Paging
	2. handlePageChange viene chiamato con il nuovo numero di pagina
	3. setPagingOptions({ page: newPage }) aggiorna il contesto di paginazione specificato
	4. Nel componente che usa usePaginatedApi, la modifica di pagingOptions viene rilevata
	5. L'hook verifica se è necessario fare una nuova richiesta confrontando con le opzioni precedenti
	6. Se necessario, esegue fetch() che chiama il metodo API con le nuove opzioni di paginazione
	7. Quando l'API risponde, setTotalInfo aggiorna i totali nel contesto
	8. I componenti si aggiornano per riflettere i nuovi dati e lo stato di paginazione
	*/
	const handlePageChange = (newPage: number) => {
		setPagingOptions({ page: newPage });

		// Trigger callback se fornita
		if (props.onPageChange) {
			props.onPageChange();
		}
	};

	// Crea l'interfaccia numerica della paginazione
	// Decide quanti numeri di pagina mostrare
	const renderPageNumbers = () => {
		const currentPage = pagingOptions.page;
		const pages = [];

		// Mostra sempre la prima pagina
		pages.push(
			<button
				key="page-0"
				className={`${styles.pageButton} ${currentPage === 0 ? styles.active : ''}`}
				onClick={() => handlePageChange(0)}
			>
				1
			</button>
		);

		// Se siamo oltre le prime pagine, mostra i puntini di sospensione
		if (currentPage > 3) {
			pages.push(
				<span key="ellipsis-1" className={styles.ellipsis}>
					...
				</span>
			);
		}

		// Calcola l'intervallo di pagine da mostrare intorno alla pagina corrente
		let startPage = Math.max(1, currentPage - 1);
		let endPage = Math.min(totalPages - 2, currentPage + 1);

		// Aggiusta per mostrare almeno 3 numeri di pagina (se possibile)
		if (endPage < startPage + 2) {
			endPage = Math.min(totalPages - 2, startPage + 2);
		}
		if (startPage > endPage - 2) {
			startPage = Math.max(1, endPage - 2);
		}

		// Genera i pulsanti per le pagine intermedie
		for (let i = startPage; i <= endPage; i++) {
			pages.push(
				<button
					key={`page-${i}`}
					className={`${styles.pageButton} ${currentPage === i ? styles.active : ''}`}
					onClick={() => handlePageChange(i)}
				>
					{i + 1}
				</button>
			);
		}

		// Se ci sono altre pagine dopo quelle mostrate, aggiungi puntini di sospensione
		if (endPage < totalPages - 2) {
			pages.push(
				<span key="ellipsis-2" className={styles.ellipsis}>
					...
				</span>
			);
		}

		// Mostra sempre l'ultima pagina (se ce n'è più di una)
		if (totalPages > 1) {
			pages.push(
				<button
					key={`page-${totalPages - 1}`}
					className={`${styles.pageButton} ${currentPage === totalPages - 1 ? styles.active : ''}`}
					onClick={() => handlePageChange(totalPages - 1)}
				>
					{totalPages}
				</button>
			);
		}

		return pages;
	};

	return (
		<div className={`${styles.pagination} ${props.className}`}>
			<button
				className={styles.navButton}
				onClick={() => handlePageChange(Math.max(0, pagingOptions.page - 1))}
				disabled={pagingOptions.page <= 0}
			>
				&lt;
			</button>

			<div className={styles.pageNumbers}>{renderPageNumbers()}</div>

			<button
				className={styles.navButton}
				onClick={() => handlePageChange(Math.min(totalPages - 1, pagingOptions.page + 1))}
				disabled={pagingOptions.page >= totalPages - 1}
			>
				&gt;
			</button>
		</div>
	);
};

export default Paging;
